import {Box, List, Text} from '@mantine/core';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Collaboration, Party} from 'tenants/admin/TenantRolesManagementModal';
import Label from 'ui-library/bs/Label';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

type PartyTypename = Exclude<Collaboration['party']['__typename'], 'RoutinePrincipalNode'>;

export default function TenantRoleListWithPopover({party}: {party: Party}): JSX.Element {
  // Group roles by party (key by party id)
  const partyToRoles = new Map<
    string,
    {partyName: string; __typename: PartyTypename; roleNames: Array<string>}
  >();
  party.tenantCollaborations.forEach((c: Collaboration) => {
    // Routine membership is not supported by the tenant-role management UI.
    if (c.party.__typename === 'RoutinePrincipalNode') {
      return;
    }
    let partyRoles = partyToRoles.get(c.party.id);

    if (!partyRoles) {
      partyRoles = {
        partyName: c.party.name,
        __typename: c.party.__typename,
        roleNames: [],
      };
      partyToRoles.set(c.party.id, partyRoles);
    }
    partyRoles.roleNames.push(c.policy.name);
  });

  // Order by increasing hierarchy level
  const sortedPartyRoles = Array.from(partyToRoles.entries()).sort(
    ([_, a], [__, b]) => PartyHierarchyLevel[a.__typename] - PartyHierarchyLevel[b.__typename]
  );
  return (
    <Popover
      interaction={FloatingInteraction.HOVER}
      renderContent={() => <TenantRolePopoverContent partyRoles={sortedPartyRoles} />}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <TenantRoleColumnContent
          partyRoles={sortedPartyRoles}
          partyLevel={PartyHierarchyLevel[party.__typename]}
        />
      </span>
    </Popover>
  );
}

function TenantRolePopoverContent({
  partyRoles,
}: {
  partyRoles: Array<[string, {partyName: string; __typename: PartyTypename; roleNames: Array<string>}]>;
}): JSX.Element {
  return (
    <List listStyleType="none" withPadding={false} spacing="md" c={GRAY_DARKER}>
      {partyRoles.map(([partyId, {partyName, __typename, roleNames}]) => (
        <PartyRoleGroup key={partyId} partyName={partyName} __typename={__typename} roleNames={roleNames} />
      ))}
    </List>
  );
}

interface PartyRoleGroupProps {
  partyName: string;
  __typename: PartyTypename;
  roleNames: Array<string>;
}

function PartyRoleGroup({partyName, __typename, roleNames}: PartyRoleGroupProps): JSX.Element {
  return (
    <List.Item>
      <Box
        component={BenchlingIcon}
        icon={PartyIconName[__typename] as BenchlingIconName}
        mr={5}
        sx={{verticalAlign: 'middle-baseline'}}
      />
      <Label>{partyName}</Label>
      <List listStyleType="none" withPadding={false} c={GRAY_DARKER}>
        {roleNames.map((roleName) => (
          <List.Item key={roleName}>{roleName}</List.Item>
        ))}
      </List>
    </List.Item>
  );
}

function TenantRoleColumnContent({
  partyRoles,
  partyLevel,
}: {
  partyRoles: Array<[string, {partyName: string; __typename: PartyTypename; roleNames: Array<string>}]>;
  partyLevel: number;
}): JSX.Element {
  const flatPartyRoles = partyRoles.flatMap(([partyId, {__typename, roleNames}]) =>
    roleNames.map((roleName) => ({
      __typename,
      roleName,
    }))
  );
  return (
    <span>
      {flatPartyRoles.map(({__typename, roleName}, index) => {
        const isPropagated = PartyHierarchyLevel[__typename] > partyLevel;
        return (
          <span key={`${__typename}-${roleName}-${index}`}>
            {index > 0 ? ', ' : null}
            <Text component="span">{roleName}</Text>
            {isPropagated && (
              <Box
                component={BenchlingIcon}
                icon={PartyIconName[__typename] as BenchlingIconName}
                ml={5}
                mr={5}
                sx={{verticalAlign: 'middle-baseline'}}
              />
            )}
          </span>
        );
      })}
    </span>
  );
}

const PartyHierarchyLevel = {
  UserNode: 0,
  ServiceAccountActorNode: 0,
  BenchlingAppNode: 0,
  TeamNode: 1,
  OrganizationNode: 2,
};

enum PartyIconName {
  TeamNode = 'team',
  OrganizationNode = 'organization',
  UserNode = 'user',
  BenchlingAppNode = ModelTypeConstants.BENCHLING_APP,
  ServiceAccountActorNode = ModelTypeConstants.SERVICE_ACCOUNT,
}
